/**
 * The map on page one of the funding request PDF, as plain data.
 *
 * A vendor cannot find the work from a lat/long, so the selected steps are
 * drawn over OSM tiles and numbered to match the "#" column of the step
 * tables. Pure, so zoom, tiles and projection are testable without rendering.
 * Reuses staticMap.ts for the Web Mercator math and tile URLs.
 */
import { TILE, OSM_ATTRIBUTION, lonToTileX, latToTileY, tileUrl } from './staticMap';
import type { StaticMapTile } from './staticMap';

export interface FundingMapFeature {
  /** The step's number in print order, matching the "#" column. */
  number: number;
  kind: 'point' | 'line' | 'area';
  coords: { lat: number; lng: number }[];
}

export interface FundingMapPoint {
  x: number;
  y: number;
}

export interface FundingMapShape {
  number: number;
  kind: 'point' | 'line' | 'area';
  points: FundingMapPoint[];
  /** Half way along a line, else the mean of the points. A point's dot is drawn here. */
  center: FundingMapPoint;
  /** Where the number label goes: `center`, moved aside if another label is already there. */
  label: FundingMapPoint;
}

export interface FundingMapSpec {
  width: number;
  height: number;
  /** The integer zoom the tiles come from. */
  tileZoom: number;
  /** The fitted zoom, fractional. Tiles are scaled by 2^(zoom - tileZoom). */
  zoom: number;
  /** Rendered tile edge in points. */
  tileSize: number;
  /** Tile offsets in points from the box's top-left; may be negative. */
  tiles: StaticMapTile[];
  shapes: FundingMapShape[];
  attribution: string;
}

/** Page content width: LETTER 612pt less 32pt padding each side (SPACING.xl). */
export const FUNDING_MAP_SIZE = { width: 548, height: 300 };
/** Matches the readiness record's zoom, so a lone step is not absurdly close. */
export const FUNDING_MAP_MAX_ZOOM = 17;

/** How far a point step's label sits from its dot, in points. */
const POINT_LABEL_OFFSET = 11;
const MIN_ZOOM = 3;
const PADDING = 28;
// Web Mercator is undefined at the poles; OSM stops at about this latitude.
const MAX_LAT = 85.0511;

const clampLat = (lat: number) => Math.max(-MAX_LAT, Math.min(MAX_LAT, lat));

/** Largest zoom (capped) at which the bounds fit the box inside the padding. */
export function fitZoom(
  coords: { lat: number; lng: number }[],
  width: number,
  height: number,
  padding = PADDING
): number {
  if (coords.length === 0) return MIN_ZOOM;
  let n = -Infinity;
  let s = Infinity;
  let e = -Infinity;
  let w = Infinity;
  for (const { lat, lng } of coords) {
    n = Math.max(n, clampLat(lat));
    s = Math.min(s, clampLat(lat));
    e = Math.max(e, lng);
    w = Math.min(w, lng);
  }
  // Extent in pixels at zoom 0 (one 256px world); it doubles with each zoom.
  const spanX = (lonToTileX(e, 0) - lonToTileX(w, 0)) * TILE;
  const spanY = (latToTileY(s, 0) - latToTileY(n, 0)) * TILE;
  const availX = Math.max(1, width - 2 * padding);
  const availY = Math.max(1, height - 2 * padding);
  const zx = spanX > 0 ? Math.log2(availX / spanX) : Infinity;
  const zy = spanY > 0 ? Math.log2(availY / spanY) : Infinity;
  const fit = Math.min(zx, zy);
  return Math.max(MIN_ZOOM, Math.min(FUNDING_MAP_MAX_ZOOM, fit));
}

function meanPoint(points: FundingMapPoint[]): FundingMapPoint {
  return {
    x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
    y: points.reduce((sum, p) => sum + p.y, 0) / points.length,
  };
}

/** The point half way along a line, so its label sits on the line, not beside a bend. */
function lineMidpoint(points: FundingMapPoint[]): FundingMapPoint {
  if (points.length < 2) return meanPoint(points);
  const lengths = points.slice(1).map((p, i) => Math.hypot(p.x - points[i].x, p.y - points[i].y));
  let remaining = lengths.reduce((a, b) => a + b, 0) / 2;
  for (let i = 0; i < lengths.length; i++) {
    if (remaining <= lengths[i] || i === lengths.length - 1) {
      const t = lengths[i] === 0 ? 0 : remaining / lengths[i];
      return {
        x: points[i].x + (points[i + 1].x - points[i].x) * t,
        y: points[i].y + (points[i + 1].y - points[i].y) * t,
      };
    }
    remaining -= lengths[i];
  }
  return meanPoint(points);
}

/** Label diameter plus a hairline of air, in points. Two labels closer than this overlap. */
export const FUNDING_MAP_LABEL_GAP = 18;

/**
 * Moves a label aside when an earlier one already sits where it wants to go.
 * Two steps drawn over the same shape (a fire break copied as fuel removal)
 * share a center, so the second number used to print on top of the first.
 * Each label tries its own spot, then steps right one gap at a time, wrapping
 * to the left edge of the next row down if it runs out of box.
 */
export function spreadLabels(
  wanted: FundingMapPoint[],
  { width, height }: { width: number; height: number },
  gap = FUNDING_MAP_LABEL_GAP
): FundingMapPoint[] {
  const placed: FundingMapPoint[] = [];
  const edge = gap / 2;
  const clear = (p: FundingMapPoint) =>
    placed.every((q) => Math.hypot(p.x - q.x, p.y - q.y) >= gap);
  for (const want of wanted) {
    let spot = { x: want.x, y: want.y };
    // Bounded: a box holds only so many labels, and a crowded one keeps the last try.
    for (let tries = 0; tries < 200 && !clear(spot); tries++) {
      spot = { x: spot.x + gap, y: spot.y };
      if (spot.x > width - edge) spot = { x: edge, y: Math.min(height - edge, spot.y + gap) };
    }
    placed.push(spot);
  }
  return placed;
}

export function buildFundingMap(
  features: FundingMapFeature[],
  { width, height }: { width: number; height: number } = FUNDING_MAP_SIZE
): FundingMapSpec | null {
  const usable = features.filter((f) => f.coords.length > 0);
  if (usable.length === 0) return null;
  const all = usable.flatMap((f) => f.coords);

  const zoom = fitZoom(all, width, height);
  const tileZoom = Math.floor(zoom);
  const tileSize = TILE * 2 ** (zoom - tileZoom);
  const worldPx = TILE * 2 ** zoom; // the whole world in points at this zoom

  const px = (c: { lat: number; lng: number }) => ({
    x: lonToTileX(c.lng, zoom) * TILE,
    y: latToTileY(clampLat(c.lat), zoom) * TILE,
  });
  const pxs = all.map(px);
  const minX = Math.min(...pxs.map((p) => p.x));
  const maxX = Math.max(...pxs.map((p) => p.x));
  const minY = Math.min(...pxs.map((p) => p.y));
  const maxY = Math.max(...pxs.map((p) => p.y));
  // Centre the bounds in the box; keep the top edge on the map.
  const originX = (minX + maxX) / 2 - width / 2;
  const originY = Math.max(0, Math.min(worldPx - height, (minY + maxY) / 2 - height / 2));

  const centered = usable.map((f) => {
    const points = f.coords.map((c) => {
      const p = px(c);
      return { x: p.x - originX, y: p.y - originY };
    });
    const center = f.kind === 'line' ? lineMidpoint(points) : meanPoint(points);
    return { number: f.number, kind: f.kind, points, center };
  });
  // A point's dot sits at its center, so its label starts up and right of it.
  const wanted = centered.map((s) =>
    s.kind === 'point'
      ? { x: s.center.x + POINT_LABEL_OFFSET, y: s.center.y - POINT_LABEL_OFFSET }
      : s.center
  );
  const labels = spreadLabels(wanted, { width, height });
  const shapes: FundingMapShape[] = centered.map((s, i) => ({ ...s, label: labels[i] }));

  // Tiles are numbered at tileZoom; one covers `tileSize` points of the box.
  const maxIndex = 2 ** tileZoom;
  const firstX = Math.floor(originX / tileSize);
  const lastX = Math.floor((originX + width - 1e-6) / tileSize);
  const firstY = Math.max(0, Math.floor(originY / tileSize));
  const lastY = Math.min(maxIndex - 1, Math.floor((originY + height - 1e-6) / tileSize));
  const tiles: StaticMapTile[] = [];
  for (let ty = firstY; ty <= lastY; ty++) {
    for (let tx = firstX; tx <= lastX; tx++) {
      const wrapped = ((tx % maxIndex) + maxIndex) % maxIndex;
      tiles.push({
        url: tileUrl(tileZoom, wrapped, ty),
        left: tx * tileSize - originX,
        top: ty * tileSize - originY,
      });
    }
  }

  return { width, height, tileZoom, zoom, tileSize, tiles, shapes, attribution: OSM_ATTRIBUTION };
}
